import React, { useState, useEffect } from 'react';
import { collection, query, orderBy, limit, getDocs } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import { db, auth } from '../firebase/config';
import { ClipboardList, Plus, Clock, CheckCircle2, Wrench } from 'lucide-react';

/**
 * 테스트보철 공개 게시판 (/test-cases)
 * - 비로그인 포함 누구나 열람 가능 (케이스 내용만 공개, 치과/환자 정보 비공개)
 * - 치과: A/S 보철을 무료로 의뢰 작성
 * - 기공소: 케이스 수락 (상세 페이지에서)
 */
const STATUS_LABEL = {
    open: { text: '모집중', color: '#059669', bg: '#ECFDF5' },
    accepted: { text: '매칭완료', color: '#2563EB', bg: '#EFF6FF' },
    in_progress: { text: '제작중', color: '#D97706', bg: '#FFFBEB' },
    delivered: { text: '납품완료', color: '#7C3AED', bg: '#F5F3FF' },
    completed: { text: '완료', color: '#6B7280', bg: '#F3F4F6' },
    cancelled: { text: '취소됨', color: '#DC2626', bg: '#FEF2F2' },
};

function TestCaseBoard({ user }) {
    const navigate = useNavigate();
    const [requests, setRequests] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState('all'); // 기본: 전체 (모집중 상단 고정)

    const businessType = user?.businessType || user?.userType;
    const isDental = businessType === 'dental' || businessType === 'clinic';
    const isLab = businessType === 'lab' || businessType === 'outsource';

    useEffect(() => {
        const fetchRequests = async () => {
            try {
                const q = query(
                    collection(db, 'testRequests'),
                    orderBy('createdAt', 'desc'),
                    limit(100)
                );
                const snap = await getDocs(q);
                setRequests(snap.docs.map(d => ({ id: d.id, ...d.data() })));
            } catch (e) {
                console.error('테스트보철 목록 조회 실패:', e);
            } finally {
                setLoading(false);
            }
        };
        fetchRequests();
    }, []);

    // 이미 연결된 거래처의 게시글 숨김 + 초대제(컨시어지) 케이스는 목록 비노출
    const myIds = [auth.currentUser?.uid, user?.companyId].filter(Boolean);
    const visible = requests.filter(r =>
        !(r.excludedLabIds || []).some(id => myIds.includes(id)) &&
        (!r.inviteOnly || r.invitedLabId === auth.currentUser?.uid || r.invitedLabId === user?.companyId)
    );

    // 🎯 우리 기공소 지정 의뢰 — 일반 목록과 분리해 최상단 노출 (치과명 공개)
    const targeted = isLab ? visible.filter(r =>
        r.inviteOnly && (r.invitedLabId === auth.currentUser?.uid || r.invitedLabId === user?.companyId)
    ).sort((a, b) => {
        const aOpen = a.status === 'open' ? 0 : 1;
        const bOpen = b.status === 'open' ? 0 : 1;
        if (aOpen !== bOpen) return aOpen - bOpen;
        return (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0);
    }) : [];
    const targetedIds = new Set(targeted.map(r => r.id));

    const filtered = visible.filter(r => !targetedIds.has(r.id)).filter(r => {
        if (filter === 'all') return true;
        if (filter === 'open') return r.status === 'open';
        return r.status !== 'open';
    }).sort((a, b) => {
        // 모집중이 항상 최상단 (그 안에서 최신순), 마감은 그 아래 (최신순)
        const aOpen = a.status === 'open' ? 0 : 1;
        const bOpen = b.status === 'open' ? 0 : 1;
        if (aOpen !== bOpen) return aOpen - bOpen;
        return (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0);
    });

    const formatDate = (ts) => {
        if (!ts?.toDate) return '';
        const d = ts.toDate();
        return `${d.getFullYear()}.${String(d.getMonth() + 1).padStart(2, '0')}.${String(d.getDate()).padStart(2, '0')}`;
    };

    return (
        <div style={{ maxWidth: 960, margin: '0 auto', padding: '24px 16px' }}>
            {/* 헤더 */}
            <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 8 }}>
                <h1 style={{ fontSize: 24, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8, margin: 0 }}>
                    <ClipboardList size={26} color="#2563EB" /> {isDental ? 'A/S 보철 의뢰 및 테스트보철 의뢰' : '테스트보철 케이스'}
                </h1>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    {isDental && (
                        <button
                            onClick={() => navigate('/test-cases/create')}
                            style={{ display: 'flex', alignItems: 'center', gap: 6, background: '#2563EB', color: '#fff', border: 'none', padding: '10px 16px', borderRadius: 8, fontWeight: 600, fontSize: 14, cursor: 'pointer' }}
                        >
                            <Plus size={16} /> A/S 보철 의뢰하기
                        </button>
                    )}
                    {!user && (
                        <button
                            onClick={() => navigate('/login')}
                            style={{ background: '#2563EB', color: '#fff', border: 'none', padding: '10px 16px', borderRadius: 8, fontWeight: 600, fontSize: 14, cursor: 'pointer' }}
                        >
                            로그인하고 참여하기
                        </button>
                    )}
                </div>
            </div>
            {isDental ? (
                <p style={{ color: '#6B7280', fontSize: 14, marginBottom: 20, lineHeight: 1.7 }}>
                    A/S 재제작 상담은 <b>덴탈클러스터를 통해 진행된 의뢰서를 기반</b>으로 진행됩니다.
                    A/S가 필요한 보철을 부담 없이 의뢰하세요 — 기공소에는 테스트보철 케이스로 전달되어
                    정성껏 제작되며, 마음에 드는 기공소를 만나면 그대로 정식 거래처로 이어갈 수 있습니다.
                    치과·환자 정보는 기공소가 수락하기 전까지 비공개입니다.
                </p>
            ) : (
                <p style={{ color: '#6B7280', fontSize: 14, marginBottom: 20, lineHeight: 1.7 }}>
                    치과가 올린 <b>테스트보철(A/S) 케이스</b>입니다.
                    새로운 치과 거래처와 연결될 기회 — 테스트보철물 제작으로 실력을 보여주세요.
                    {isLab && <>
                        <br /><b>· 수락하면 담당자(010-2972-3733)가 치과 정보와 진행 방법을 안내해드립니다.</b>
                        <br /><b>· 수락 건수는 자동 집계되며, 건당 이용료 10,000원이 청구됩니다.</b>
                        <br /><b>· 매칭 수수료는 없습니다 (2026-08-17부로 중개 수수료 전면 폐지).</b>
                    </>}
                </p>
            )}

            {/* 필터 탭 */}
            <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
                {[['open', '모집중'], ['closed', '마감'], ['all', '전체']].map(([key, label]) => (
                    <button
                        key={key}
                        onClick={() => setFilter(key)}
                        style={{
                            padding: '8px 16px', borderRadius: 20, fontSize: 14, fontWeight: 600, cursor: 'pointer',
                            border: filter === key ? '1px solid #2563EB' : '1px solid #E5E7EB',
                            background: filter === key ? '#EFF6FF' : '#fff',
                            color: filter === key ? '#2563EB' : '#6B7280',
                        }}
                    >
                        {label}
                    </button>
                ))}
            </div>

            {/* 🎯 우리 기공소 지정 의뢰 — 최상단 별도 섹션 */}
            {targeted.length > 0 && (
                <div style={{ marginBottom: 20, border: '2px solid #6366F1', borderRadius: 12, overflow: 'hidden', background: '#fff' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '12px 16px', background: '#EEF2FF', borderBottom: '1px solid #C7D2FE', fontWeight: 800, fontSize: 15, color: '#3730A3' }}>
                        🎯 우리 기공소 지정 의뢰 <span style={{ fontSize: 12, fontWeight: 600, color: '#6366F1' }}>치과가 프로필을 보고 직접 의뢰한 케이스 — 다른 기공소에는 보이지 않습니다</span>
                    </div>
                    <div style={{ overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
                            <thead>
                                <tr style={{ background: '#F9FAFB', borderBottom: '1px solid #E5E7EB' }}>
                                    {['상태', '의뢰 치과', '테스트번호', '보철 종류', '치식', '재료', '희망납기', '등록일'].map(h => (
                                        <th key={h} style={{ padding: '11px 14px', textAlign: 'left', fontSize: 12.5, fontWeight: 700, color: '#6B7280', whiteSpace: 'nowrap' }}>{h}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {targeted.map(r => {
                                    const s = STATUS_LABEL[r.status] || STATUS_LABEL.open;
                                    const c = r.caseInfo || {};
                                    const isOpen = r.status === 'open';
                                    return (
                                        <tr key={r.id} onClick={() => navigate(`/test-cases/${r.id}`)}
                                            style={{ cursor: 'pointer', background: isOpen ? '#F5F3FF' : '#fff', opacity: isOpen ? 1 : 0.62, borderTop: '1px solid #F3F4F6' }}
                                            onMouseEnter={e => e.currentTarget.style.background = isOpen ? '#EDE9FE' : '#F9FAFB'}
                                            onMouseLeave={e => e.currentTarget.style.background = isOpen ? '#F5F3FF' : '#fff'}>
                                            <td style={{ padding: '11px 14px', whiteSpace: 'nowrap' }}>
                                                <span style={{ fontSize: 12, fontWeight: 700, color: s.color, background: s.bg, padding: '4px 10px', borderRadius: 12 }}>
                                                    {isOpen ? '● 모집중' : s.text}
                                                </span>
                                            </td>
                                            <td style={{ padding: '11px 14px', fontWeight: 800, color: '#3730A3', whiteSpace: 'nowrap' }}>🏥 {r.invitedClinicName || '지정 의뢰 (수락 후 공개)'}</td>
                                            <td style={{ padding: '11px 14px', fontWeight: 700, color: '#111827', whiteSpace: 'nowrap' }}>{r.testNumber}</td>
                                            <td style={{ padding: '11px 14px', fontWeight: 600 }}>{c.prosthesisType || '-'}</td>
                                            <td style={{ padding: '11px 14px', color: '#6B7280' }}>{c.teeth?.length > 0 ? c.teeth.join(', ') : '-'}</td>
                                            <td style={{ padding: '11px 14px', color: '#6B7280' }}>{c.material || '-'}</td>
                                            <td style={{ padding: '11px 14px', color: '#6B7280', whiteSpace: 'nowrap' }}>{c.dueDate || '-'}</td>
                                            <td style={{ padding: '11px 14px', color: '#9CA3AF', whiteSpace: 'nowrap' }}>{formatDate(r.createdAt)}</td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* 목록 */}
            {loading ? (
                <div style={{ textAlign: 'center', padding: 60, color: '#9CA3AF' }}>불러오는 중...</div>
            ) : filtered.length === 0 ? (
                <div style={{ textAlign: 'center', padding: 60, color: '#9CA3AF', background: '#F9FAFB', borderRadius: 12 }}>
                    {filter === 'open' ? '현재 모집중인 테스트 케이스가 없습니다.' : '해당하는 케이스가 없습니다.'}
                </div>
            ) : (
                <div style={{ border: '1px solid #E5E7EB', borderRadius: 12, overflow: 'hidden', background: '#fff' }}>
                    <div style={{ overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
                            <thead>
                                <tr style={{ background: '#F9FAFB', borderBottom: '1px solid #E5E7EB' }}>
                                    {['상태', '테스트번호', '보철 종류', '치식', '재료', '희망납기', '지역', '등록일'].map(h => (
                                        <th key={h} style={{ padding: '11px 14px', textAlign: 'left', fontSize: 12.5, fontWeight: 700, color: '#6B7280', whiteSpace: 'nowrap' }}>{h}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {filtered.map((r, idx) => {
                                    const s = STATUS_LABEL[r.status] || STATUS_LABEL.open;
                                    const c = r.caseInfo || {};
                                    const isOpen = r.status === 'open';
                                    // 모집중 구역 끝 → 마감 구역 시작 지점에 굵은 구분선
                                    const prevOpen = idx > 0 && filtered[idx - 1].status === 'open';
                                    const sectionBreak = !isOpen && prevOpen;
                                    return (
                                        <tr
                                            key={r.id}
                                            onClick={() => navigate(`/test-cases/${r.id}`)}
                                            style={{
                                                cursor: 'pointer',
                                                background: isOpen ? '#F0FDF4' : '#fff',
                                                opacity: isOpen ? 1 : 0.62,
                                                borderTop: sectionBreak ? '3px solid #D1D5DB' : '1px solid #F3F4F6',
                                            }}
                                            onMouseEnter={e => e.currentTarget.style.background = isOpen ? '#DCFCE7' : '#F9FAFB'}
                                            onMouseLeave={e => e.currentTarget.style.background = isOpen ? '#F0FDF4' : '#fff'}
                                        >
                                            <td style={{ padding: '11px 14px', whiteSpace: 'nowrap' }}>
                                                <span style={{ fontSize: 12, fontWeight: 700, color: s.color, background: s.bg, padding: '4px 10px', borderRadius: 12, border: isOpen ? '1px solid #A7F3D0' : 'none' }}>
                                                    {isOpen ? '● 모집중' : s.text}
                                                </span>
                                            </td>
                                            <td style={{ padding: '11px 14px', fontWeight: 700, color: '#111827', whiteSpace: 'nowrap' }}>{r.testNumber}</td>
                                            <td style={{ padding: '11px 14px', fontWeight: 600 }}>{c.prosthesisType || '-'}</td>
                                            <td style={{ padding: '11px 14px', color: '#6B7280' }}>{c.teeth?.length > 0 ? c.teeth.join(', ') : '-'}</td>
                                            <td style={{ padding: '11px 14px', color: '#6B7280' }}>{c.material || '-'}</td>
                                            <td style={{ padding: '11px 14px', color: '#6B7280', whiteSpace: 'nowrap' }}>{c.dueDate || '-'}</td>
                                            <td style={{ padding: '11px 14px', color: '#6B7280', whiteSpace: 'nowrap' }}>{r.region || '-'}</td>
                                            <td style={{ padding: '11px 14px', color: '#9CA3AF', whiteSpace: 'nowrap' }}>{formatDate(r.createdAt)}</td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
}

export default TestCaseBoard;
